import { useState } from 'preact/hooks';
import { menuFor } from '../../shared/rules/menu';
import { useI18n, type MessageKey } from '../i18n';
import type { ShellUser } from '../lib/session';
import { applyTheme, initialTheme, type Theme } from '../lib/theme';
import { AccountMenu } from './AccountMenu';
import { Bell } from './Bell';
import { HeaderSearch } from './HeaderSearch';

interface Props {
  user: ShellUser;
  path: string;
  /** Absent while nobody is signed in. */
  onSignOut?: (() => void) | undefined;
}

export function AppHeader({ user, path, onSignOut }: Props) {
  const { lang, t, setLang } = useI18n();
  const menus = menuFor(user.roles, user.poApprover === true);
  const [theme, setTheme] = useState<Theme>(initialTheme);
  const toggleTheme = () => {
    const next: Theme = theme === 'dark' ? 'light' : 'dark';
    applyTheme(next);
    setTheme(next);
  };

  return (
    <header class="app-header">
      <div class="app-header__row">
        <a class="brand" href="#/">
          <img
            class="brand__logo brand__logo--light"
            src="/brand/smc-group.svg"
            alt="SMC Group"
            width="77"
            height="30"
          />
          <img
            class="brand__logo brand__logo--dark"
            src="/brand/smc-group-dark.svg"
            alt="SMC Group"
            width="77"
            height="30"
          />
          <span class="brand__word">{t('app.name')}</span>
        </a>
        {/* No menu or account controls until someone is signed in (Fahad, 2 Nov). */}
        {onSignOut && (
          <nav class="app-nav" aria-label={t('nav.label')}>
            {menus.top.map((item) => (
              <a
                key={item.id}
                href={`#${item.path}`}
                aria-current={path === item.path ? 'page' : undefined}
              >
                {t(`nav.${item.id}` as MessageKey)}
              </a>
            ))}
          </nav>
        )}
        <div class="app-header__end">
          {onSignOut && <HeaderSearch roles={user.roles} />}
          {onSignOut && (
            <Bell roles={user.roles} poApprover={user.poApprover === true} path={path} />
          )}
          <button
            type="button"
            class="lang-switch theme-switch"
            aria-pressed={theme === 'dark'}
            aria-label={t('theme.switchLabel')}
            title={t(theme === 'dark' ? 'theme.light' : 'theme.dark')}
            onClick={toggleTheme}
          >
            <span aria-hidden="true">{theme === 'dark' ? '☀' : '☾'}</span>
          </button>
          <button
            type="button"
            class="lang-switch"
            lang={lang === 'en' ? 'ar' : 'en'}
            aria-label={t('lang.switchLabel')}
            onClick={() => setLang(lang === 'en' ? 'ar' : 'en')}
          >
            {t('lang.switch')}
          </button>
          {onSignOut && (
            <AccountMenu user={user} items={menus.account} path={path} onSignOut={onSignOut} />
          )}
        </div>
      </div>
      <div class="entity-stripe" aria-hidden="true">
        <span class="entity-stripe__smc" />
        <span class="entity-stripe__vibes" />
        <span class="entity-stripe__media" />
      </div>
    </header>
  );
}
